import { commandItemIconClass } from "@/components/command-palette/constants";
import { CommandGroup, CommandItem } from "@/components/flexkit/Command";
import { useMembership } from "@/hooks/use-membership";
import { IconKey1 } from "central-icons/IconKey1";
import { IconLock } from "central-icons/IconLock";
import { IconMegaphone } from "central-icons/IconMegaphone";
import { IconPeople } from "central-icons/IconPeople";
import { IconPuzzle } from "central-icons/IconPuzzle";
import { IconRobot } from "central-icons/IconRobot";
import { IconServer } from "central-icons/IconServer";
import { IconSettingsGear2 } from "central-icons/IconSettingsGear2";
import { IconShield } from "central-icons/IconShield";
import { IconWindow } from "central-icons/IconWindow";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useMemo, type FC, type ReactNode } from "react";
import type { NavigateFunction } from "react-router-dom";

// Matches the Organization section in settingsRoutes (app.tsx) — keep in sync.
const orgSettingsTabs: { label: string; path: string; adminOnly: boolean; icon: ReactNode }[] = [
    { label: "General", path: "/settings/manage-organization", adminOnly: true, icon: <IconSettingsGear2 /> },
    { label: "Announcements", path: "/settings/announcements", adminOnly: true, icon: <IconMegaphone /> },
    { label: "Members", path: "/settings/members", adminOnly: false, icon: <IconPeople /> },
    { label: "Runners", path: "/settings/runners", adminOnly: false, icon: <IconServer /> },
    { label: "Environments", path: "/settings/environments", adminOnly: true, icon: <IconWindow /> },
    { label: "Secrets", path: "/settings/organization-secrets", adminOnly: true, icon: <IconKey1 /> },
    { label: "Agents", path: "/settings/agents", adminOnly: true, icon: <IconRobot /> },
    { label: "Integrations", path: "/settings/org-integrations", adminOnly: true, icon: <IconPuzzle /> },
    { label: "Policies", path: "/settings/policies", adminOnly: true, icon: <IconShield /> },
    { label: "Login and security", path: "/settings/login", adminOnly: true, icon: <IconLock /> },
];

type OrgSettingsPageProps = {
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
};

export const OrgSettingsPage: FC<OrgSettingsPageProps> = ({ runCommand, navigate }) => {
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    const visibleTabs = useMemo(() => orgSettingsTabs.filter((tab) => !tab.adminOnly || isOrgAdmin), [isOrgAdmin]);

    const handleSelect = useCallback(
        (path: string) => {
            runCommand(() => navigate(path));
        },
        [runCommand, navigate],
    );

    return (
        <CommandGroup heading="Organization Settings">
            {visibleTabs.map((tab) => (
                <CommandItem key={tab.path} value={`org ${tab.label}`} onSelect={() => handleSelect(tab.path)}>
                    <span className={commandItemIconClass}>{tab.icon}</span>
                    <span>{tab.label}</span>
                </CommandItem>
            ))}
        </CommandGroup>
    );
};
